import { useEffect, useMemo, useState } from 'react';
import { Ban, Receipt, Search, X } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { apiClient } from '../../lib/api';

type InvoiceStatus = 'DRAFT' | 'ISSUED' | 'CANCELLED';
type SinStatus = 'PENDING' | 'RECEIVED' | 'ACCEPTED' | 'REJECTED' | 'ANNULLED';
type InvoiceStatusFilter = 'TODOS' | InvoiceStatus;

interface InvoiceItem {
    siatInvoiceHeaderId: number;
    invoiceNumber: string;
    sinInvoiceNumber: string;
    invoiceType: string;
    invoiceStatus: InvoiceStatus | '';
    sinStatus: SinStatus | '';
    issueDateTime: string;
    totalAmount: number;
    currencyCode: string;
    customerName: string;
    bookIdentifier: string;
    cuf: string;
    pdfAvailable: boolean;
    issuerNit: number;
    issuerBusinessName: string;
    emissionMode: string;
}

interface InvoiceSummary {
    totalInvoices: number;
    totalAmount: number;
    currencyCode: string;
    issuedCount: number;
    cancelledCount: number;
    pendingWithSinCount: number;
}

interface PaginationData {
    totalItemCount: number;
    totalPageCount: number;
    pageSize: number;
    currentPage: number;
    hasPrevious: boolean;
    hasNext: boolean;
}

interface InvoiceApiResponse {
    statusCode: string;
    data: {
        items: InvoiceItem[];
        summary: InvoiceSummary;
        pagination: PaginationData;
    };
    hasError: boolean;
    resultError: {
        code: string;
        message: string;
        title: string;
        correlationId: string;
    };
}

interface InvoiceRequest {
    invoiceType: string;
    invoiceStatus: string;
    dateFrom: string;
    dateTo: string;
    search: string;
    pageNumber: number;
    pageSize: number;
}

const DEFAULT_PAGE_SIZE = 10;

const ESTADO_STYLES: Record<string, string> = {
    DRAFT: 'bg-amber-50 text-amber-700 border-amber-100',
    ISSUED: 'bg-emerald-50 text-emerald-700 border-emerald-100',
    CANCELLED: 'bg-gray-100 text-gray-700 border-gray-200',
    PENDING: 'bg-sky-50 text-sky-700 border-sky-100',
    RECEIVED: 'bg-emerald-50 text-emerald-700 border-emerald-100',
    ACCEPTED: 'bg-emerald-50 text-emerald-700 border-emerald-100',
    REJECTED: 'bg-red-50 text-red-700 border-red-100',
    ANNULLED: 'bg-gray-100 text-gray-700 border-gray-200',
};

const EMPTY_PAGINATION: PaginationData = {
    totalItemCount: 0,
    totalPageCount: 0,
    pageSize: DEFAULT_PAGE_SIZE,
    currentPage: 0,
    hasPrevious: false,
    hasNext: false,
};

const EMPTY_SUMMARY: InvoiceSummary = {
    totalInvoices: 0,
    totalAmount: 0,
    currencyCode: 'BOB',
    issuedCount: 0,
    cancelledCount: 0,
    pendingWithSinCount: 0,
};

const formatDateInput = (date: Date) => {
    const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    return localDate.toISOString().slice(0, 10);
};

const formatDisplayDate = (value: string) => {
    if (!value) {
        return 'Sin fecha';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return value;
    }

    return date.toLocaleDateString('es-BO', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    });
};

const buildRequest = ({
    invoiceStatus,
    dateFrom,
    dateTo,
    search,
    pageNumber,
    pageSize,
}: {
    invoiceStatus: InvoiceStatusFilter;
    dateFrom: string;
    dateTo: string;
    search: string;
    pageNumber: number;
    pageSize: number;
}): InvoiceRequest => ({
    invoiceType: '',
    invoiceStatus: invoiceStatus === 'TODOS' ? '' : invoiceStatus,
    dateFrom: dateFrom || formatDateInput(new Date()),
    dateTo: dateTo || formatDateInput(new Date()),
    search: typeof search === 'string' ? search.trim() : '',
    pageNumber,
    pageSize,
});

const FacturacionPage = () => {
    const today = formatDateInput(new Date());
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedStatus, setSelectedStatus] = useState<InvoiceStatusFilter>('TODOS');
    const [dateFrom, setDateFrom] = useState(today);
    const [dateTo, setDateTo] = useState(today);
    const [currentRequest, setCurrentRequest] = useState<InvoiceRequest>(() =>
        buildRequest({
            invoiceStatus: 'TODOS',
            dateFrom: today,
            dateTo: today,
            search: '',
            pageNumber: 0,
            pageSize: DEFAULT_PAGE_SIZE,
        })
    );
    const [invoices, setInvoices] = useState<InvoiceItem[]>([]);
    const [pagination, setPagination] = useState<PaginationData>(EMPTY_PAGINATION);
    const [summary, setSummary] = useState<InvoiceSummary>(EMPTY_SUMMARY);
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [isAnnulModalOpen, setIsAnnulModalOpen] = useState(false);
    const [annulInvoiceId, setAnnulInvoiceId] = useState<number | null>(null);
    const [cancellationReasonCode, setCancellationReasonCode] = useState('1');
    const [cancellationReason, setCancellationReason] = useState('');
    const [annulErrorMessage, setAnnulErrorMessage] = useState<string | null>(null);

    const loadInvoices = async (request: InvoiceRequest) => {
        setIsLoading(true);
        setErrorMessage(null);

        try {
            const response = await apiClient.post<InvoiceApiResponse>('SiatInvoice/Admin', request);

            if (response?.resultError?.code !== '200' && response?.statusCode !== "200") {
                setInvoices([]);
                setPagination(EMPTY_PAGINATION);
                setErrorMessage(response?.resultError?.message || 'No se pudo recuperar la información.');
                return;
            }

            setInvoices(response?.data?.items ?? []);
            setPagination(response?.data?.pagination ?? EMPTY_PAGINATION);
            setSummary(response?.data?.summary ?? EMPTY_SUMMARY);
        } catch (error: unknown) {
            const message =
                error instanceof Error
                    ? error.message
                    : 'Error al consultar la información de facturación.';

            setInvoices([]);
            setPagination(EMPTY_PAGINATION);
            setErrorMessage(message);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        const initialRequest = buildRequest({
            invoiceStatus: 'TODOS',
            dateFrom: today,
            dateTo: today,
            search: '',
            pageNumber: 0,
            pageSize: DEFAULT_PAGE_SIZE,
        });

        setCurrentRequest(initialRequest);
        void loadInvoices(initialRequest);
    }, [today]);

    const totalFacturado = useMemo(
        () => invoices.reduce((acc, item) => acc + (Number(item.totalAmount) || 0), 0),
        [invoices]
    );

    const totalEmitidas = useMemo(
        () => invoices.filter((item) => item.invoiceStatus === 'ISSUED').length,
        [invoices]
    );

    const applyFilters = (searchValue?: string) => {
        const nextSearch = searchValue ?? searchQuery;

        if (dateFrom && dateTo && dateFrom > dateTo) {
            setErrorMessage('La fecha inicial no puede ser mayor que la fecha final.');
            return;
        }

        const nextRequest = buildRequest({
            invoiceStatus: selectedStatus,
            dateFrom,
            dateTo,
            search: nextSearch,
            pageNumber: 0,
            pageSize: DEFAULT_PAGE_SIZE,
        });

        setCurrentRequest(nextRequest);
        void loadInvoices(nextRequest);
    };

    const handlePageChange = (nextPage: number) => {
        if (nextPage < 0 || nextPage >= pagination.totalPageCount) {
            return;
        }

        const nextRequest = {
            ...currentRequest,
            pageNumber: nextPage,
        };

        setCurrentRequest(nextRequest);
        void loadInvoices(nextRequest);
    };

    const handleRetryInvoice = async (siatInvoiceHeaderId: number) => {
        try {
            setErrorMessage(null);
            const response = await apiClient.post(`SiatInvoice/Admin/${siatInvoiceHeaderId}/Retry`);
            console.log(response);
            if (response?.resultError?.code !== '200' || response?.statusCode !== "200") {
                const message = response?.resultError?.message || 'No se pudo reintentar la factura.';
                setErrorMessage(message);
            }else {
                setErrorMessage(null);
                await loadInvoices(currentRequest);
            }            
        } catch (error: unknown) {
            const message =
                error instanceof Error
                    ? error.message
                    : error?.resultError?.message ||'No se pudo reintentar la factura.';

            setErrorMessage(message);
        }
    };

    const closeAnnulModal = () => {
        setIsAnnulModalOpen(false);
        setAnnulInvoiceId(null);
        setCancellationReasonCode('1');
        setCancellationReason('');
        setAnnulErrorMessage(null);
    };

    const openAnnulModal = (siatInvoiceHeaderId: number) => {
        setAnnulInvoiceId(siatInvoiceHeaderId);
        setCancellationReasonCode('1');
        setCancellationReason('');
        setAnnulErrorMessage(null);
        setIsAnnulModalOpen(true);
    };

    const handleAnnulInvoice = async (
        siatInvoiceHeaderId: number,
        selectedReasonCode: string = '1',
        description: string = ''
    ) => {
        if (!siatInvoiceHeaderId) {
            return;
        }

        const trimmedDescription = description.trim();
        if (!trimmedDescription) {
            setAnnulErrorMessage('Debe ingresar una descripción para anular la factura.');
            return;
        }

        try {
            setAnnulErrorMessage(null);
            const response = await apiClient.post(`SiatInvoice/Admin/${siatInvoiceHeaderId}/Annul`, {
                cancellationReasonCode: selectedReasonCode,
                cancellationReason: trimmedDescription,
            });

            if (response?.resultError?.code !== '200' || response?.statusCode !== '200') {
                const message = response?.resultError?.message || 'No se pudo anular la factura.';
                setAnnulErrorMessage(message);
                return;
            }

            setAnnulErrorMessage(null);
            closeAnnulModal();
            await loadInvoices(currentRequest);
        } catch (error: unknown) {
            const message =
                error instanceof Error
                    ? error.message
                    : error?.resultError?.message || 'No se pudo anular la factura.';

            setAnnulErrorMessage(message);
        }
    };

    return (
        <div className="relative flex flex-col w-full h-screen overflow-hidden font-sans text-gray-900 bg-gray-50">
            <div className="flex flex-col flex-1 w-full h-full overflow-y-auto overflow-x-hidden hide-scrollbar bg-white">
                <div className="flex flex-col gap-4 px-6 py-5 border-b border-gray-100 sm:px-7">
                    <div className="flex items-start justify-between gap-3">
                        <div>
                            <h1 className="text-[20px] font-semibold text-gray-900">Facturación</h1>
                            <p className="mt-1 text-[12px] text-gray-500">
                                Gestión y seguimiento de facturas por filtros, búsqueda y fechas.
                            </p>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                        <div className="p-3 border border-gray-200 rounded-lg bg-gray-50/70">
                            <span className="text-[11px] font-semibold tracking-wide text-gray-500 uppercase">
                                Facturas
                            </span>
                            <p className="mt-1 text-[20px] font-semibold text-gray-900">{summary.totalInvoices || invoices.length}</p>
                        </div>
                        <div className="p-3 border border-gray-200 rounded-lg bg-gray-50/70">
                            <span className="text-[11px] font-semibold tracking-wide text-gray-500 uppercase">
                                Emitidas
                            </span>
                            <p className="mt-1 text-[20px] font-semibold text-emerald-700">{totalEmitidas}</p>
                        </div>
                        <div className="p-3 border border-gray-200 rounded-lg bg-gray-50/70">
                            <span className="text-[11px] font-semibold tracking-wide text-gray-500 uppercase">
                                Monto facturado
                            </span>
                            <p className="mt-1 text-[20px] font-semibold text-gray-900">
                                Bs {totalFacturado.toFixed(2)}
                            </p>
                        </div>
                    </div>

                    <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
                        <div className="relative w-full lg:max-w-[420px]">
                            <Search size={16} className="absolute text-gray-400 -translate-y-1/2 left-3 top-1/2" />
                            <input
                                type="text"
                                value={searchQuery}
                                onChange={(event) => setSearchQuery(event.target.value)}
                                onKeyDown={(event) => {
                                    if (event.key === 'Enter') {
                                        event.preventDefault();
                                        applyFilters();
                                    }
                                }}
                                placeholder="Buscar por número, cliente, libro o CUF"
                                className="w-full h-10 pl-9 pr-10 text-[13px] bg-white border border-gray-200 rounded-lg outline-none transition-colors placeholder:text-gray-400 focus:border-[#4f6df5]"
                            />
                            {searchQuery && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        setSearchQuery('');
                                        applyFilters('');
                                    }}
                                    className="absolute flex items-center justify-center text-gray-400 -translate-y-1/2 rounded-full right-2 top-1/2 hover:text-gray-600"
                                    aria-label="Limpiar búsqueda"
                                >
                                    <X size={14} />
                                </button>
                            )}
                        </div>

                        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                            {/* <select
                                value={selectedStatus}
                                onChange={(event) =>
                                    setSelectedStatus(event.target.value as InvoiceStatusFilter)
                                }
                                className="w-full sm:w-[180px] h-10 px-3 text-[13px] text-gray-700 bg-white border border-gray-200 rounded-lg outline-none transition-colors focus:border-[#4f6df5]"
                            >
                                <option value="TODOS">Todos los estados</option>
                                <option value="DRAFT">Draft</option>
                                <option value="ISSUED">Issued</option>
                                <option value="CANCELLED">Cancelled</option>
                            </select> */}

                            <div className="flex items-center gap-2">
                                <input
                                    type="date"
                                    value={dateFrom}
                                    onChange={(event) => setDateFrom(event.target.value)}
                                    className="h-10 px-3 text-[13px] text-gray-700 bg-white border border-gray-200 rounded-lg outline-none transition-colors focus:border-[#4f6df5]"
                                />
                                <span className="text-[12px] text-gray-500">a</span>
                                <input
                                    type="date"
                                    value={dateTo}
                                    onChange={(event) => setDateTo(event.target.value)}
                                    className="h-10 px-3 text-[13px] text-gray-700 bg-white border border-gray-200 rounded-lg outline-none transition-colors focus:border-[#4f6df5]"
                                />
                            </div>

                            <button
                                type="button"
                                onClick={() => applyFilters()}
                                className="h-10 px-4 text-[12px] font-semibold text-white bg-[#4f6df5] rounded-lg transition-colors hover:bg-[#425ae0]"
                            >
                                Aplicar filtros
                            </button>
                        </div>
                    </div>
                </div>

                {errorMessage && (
                    <div className="px-6 py-3 border-b border-red-100 bg-red-50 sm:px-7">
                        <p className="text-[12px] font-medium text-red-700">{errorMessage}</p>
                    </div>
                )}

                <div className="flex-1 w-full overflow-auto hide-scrollbar bg-white">
                    <table className="w-full min-w-[1200px] border-collapse">
                        <thead className="sticky top-0 z-10 bg-gray-50 border-b border-gray-200 shadow-sm">
                            <tr>
                                <th className="px-4 sm:px-7 py-3 text-left text-[10px] font-bold tracking-[0.8px] text-gray-500 uppercase">
                                    Factura
                                </th>
                                <th className="px-4 py-3 text-left text-[10px] font-bold tracking-[0.8px] text-gray-500 uppercase">
                                    Cliente
                                </th>
                                <th className="px-4 py-3 text-left text-[10px] font-bold tracking-[0.8px] text-gray-500 uppercase">
                                    Tipo / Estado
                                </th>
                                <th className="px-4 py-3 text-left text-[10px] font-bold tracking-[0.8px] text-gray-500 uppercase">
                                    Fecha emisión
                                </th>
                                <th className="px-4 py-3 text-left text-[10px] font-bold tracking-[0.8px] text-gray-500 uppercase">
                                    Libro / CUF
                                </th>
                                <th className="px-4 sm:px-7 py-3 text-right text-[10px] font-bold tracking-[0.8px] text-gray-500 uppercase">
                                    Monto
                                </th>
                                <th className="px-4 sm:px-7 py-3 text-right text-[10px] font-bold tracking-[0.8px] text-gray-500 uppercase">
                                    Acciones
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {isLoading ? (
                                <tr>
                                    <td colSpan={7} className="px-4 py-12 text-sm text-center text-gray-500">
                                        Cargando facturas...
                                    </td>
                                </tr>
                            ) : invoices.length === 0 ? (
                                <tr>
                                    <td colSpan={7} className="px-4 py-12 text-sm text-center text-gray-500">
                                        No se encontraron facturas para los filtros seleccionados.
                                    </td>
                                </tr>
                            ) : (
                                invoices.map((invoice) => {
                                    const status = invoice.invoiceStatus || invoice.sinStatus || 'DRAFT';
                                    const sinStatus = invoice.sinStatus || 'PENDING';
                                    const canFacturar = ['PENDING', 'RECEIVED', 'REJECTED'].includes(sinStatus);
                                    const canAnular = sinStatus === 'ACCEPTED';

                                    return (
                                        <tr
                                            key={invoice.siatInvoiceHeaderId}
                                            className="transition-colors border-b border-gray-100 hover:bg-gray-50"
                                        >
                                            <td className="px-4 sm:px-7 py-3.5 align-top">
                                                <div className="flex flex-col gap-1">
                                                    {/* <span className="text-[11px] font-mono font-medium text-gray-500">
                                                        {invoice.invoiceNumber || invoice.sinInvoiceNumber || 'Sin número'}
                                                    </span> */}
                                                    <span className="text-[13px] font-semibold text-gray-900 leading-snug">
                                                        {invoice.invoiceType || 'Factura'}
                                                    </span>
                                                    <span className="text-[11px] text-gray-500">
                                                        {invoice.emissionMode || 'Sin modalidad'}
                                                    </span>
                                                </div>
                                            </td>

                                            <td className="px-4 py-3.5 align-top">
                                                <div className="flex flex-col gap-1">
                                                    <span className="text-[13px] font-semibold text-gray-900 leading-snug">
                                                        {invoice.customerName || invoice.issuerBusinessName || 'Sin cliente'}
                                                    </span>
                                                    <span className="text-[11px] font-mono text-gray-500">
                                                        NIT: {invoice.issuerNit || 'N/A'}
                                                    </span>
                                                </div>
                                            </td>

                                            <td className="px-4 py-3.5 align-top">
                                                <div className="flex flex-col gap-2">
                                                    <span
                                                        className={`inline-flex w-fit items-center gap-1 px-2 py-0.5 text-[10px] font-bold tracking-wide uppercase rounded-full border ${ESTADO_STYLES[status] || 'bg-slate-50 text-slate-700 border-slate-200'}`}
                                                    >
                                                        {status}
                                                    </span>
                                                    <span className="text-[11px] text-gray-500">
                                                        {invoice.sinStatus || 'PENDING'}
                                                    </span>
                                                </div>
                                            </td>

                                            <td className="px-4 py-3.5 align-top">
                                                <div className="flex flex-col gap-1 text-[12px] text-gray-600">
                                                    <span>{formatDisplayDate(invoice.issueDateTime)}</span>
                                                    <span className="font-mono text-[11px] text-gray-500">
                                                        {invoice.currencyCode || 'BOB'}
                                                    </span>
                                                </div>
                                            </td>

                                            <td className="px-4 py-3.5 align-top">
                                                <div className="flex flex-col gap-1 text-[11px]">
                                                    <span className="font-mono text-gray-700">
                                                        Libro: {invoice.bookIdentifier || 'N/A'}
                                                    </span>
                                                    <span className="font-mono text-gray-500 break-all">
                                                        CUF: {invoice.cuf || 'N/A'}
                                                    </span>
                                                </div>
                                            </td>

                                            <td className="px-4 sm:px-7 py-3.5 text-right align-top">
                                                <span className="text-[14px] font-semibold text-gray-900">
                                                    Bs {Number(invoice.totalAmount || 0).toFixed(2)}
                                                </span>
                                            </td>

                                            <td className="px-4 sm:px-7 py-3.5 text-right align-top">
                                                <div className="flex items-center justify-end gap-2">
                                                    <button
                                                        type="button"
                                                        disabled={!canFacturar || !invoice.siatInvoiceHeaderId}
                                                        onClick={() => handleRetryInvoice(invoice.siatInvoiceHeaderId)}
                                                        className="inline-flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-semibold text-[#4f6df5] border border-[#4f6df5]/30 rounded-md transition-colors hover:bg-[#4f6df5]/10 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                                                    >
                                                        <Receipt size={12} />
                                                        Facturar
                                                    </button>
                                                    <button
                                                        type="button"
                                                        disabled={!canAnular || !invoice.siatInvoiceHeaderId}
                                                        onClick={() => openAnnulModal(invoice.siatInvoiceHeaderId)}
                                                        className="inline-flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-semibold text-red-600 border border-red-200 rounded-md transition-colors hover:bg-red-50 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                                                    >
                                                        <Ban size={12} />
                                                        Anular
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>

                {!isLoading && invoices.length > 0 && (
                    <div className="flex items-center justify-between gap-4 px-6 py-4 border-t border-gray-100 sm:px-7">
                        <div className="text-[12px] text-gray-500">
                            Página {pagination.currentPage + 1} de {Math.max(pagination.totalPageCount, 1)}
                        </div>

                        <div className="flex items-center gap-2">
                            <button
                                type="button"
                                onClick={() => handlePageChange(pagination.currentPage - 1)}
                                disabled={!pagination.hasPrevious}
                                className="px-3 py-1.5 text-[12px] font-medium text-gray-700 bg-white border border-gray-200 rounded-md disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                Anterior
                            </button>
                            <button
                                type="button"
                                onClick={() => handlePageChange(pagination.currentPage + 1)}
                                disabled={!pagination.hasNext}
                                className="px-3 py-1.5 text-[12px] font-medium text-gray-700 bg-white border border-gray-200 rounded-md disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                Siguiente
                            </button>
                        </div>
                    </div>
                )}
            </div>

            <Dialog
                open={isAnnulModalOpen}
                onOpenChange={(open) => {
                    if (!open) {
                        closeAnnulModal();
                    }
                }}
            >
                <DialogContent className="sm:max-w-[500px]">
                    <DialogHeader>
                        <DialogTitle>Anular factura</DialogTitle>
                        <DialogDescription>
                            Seleccione el motivo del anulado y agregue una descripción detallada.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4 py-2">
                        <div className="space-y-2">
                            <label htmlFor="cancellationReasonCode" className="text-[12px] font-medium text-gray-700">
                                Motivo de anulación
                            </label>
                            <select
                                id="cancellationReasonCode"
                                value={cancellationReasonCode}
                                onChange={(event) => {
                                    setCancellationReasonCode(event.target.value);
                                    setAnnulErrorMessage(null);
                                }}
                                className="w-full h-10 px-3 text-[13px] text-gray-700 bg-white border border-gray-200 rounded-lg outline-none transition-colors focus:border-[#4f6df5]"
                            >
                                <option value="1">factura mal emitida</option>
                                <option value="2">nota de crédito mal emitida</option>
                                <option value="3">datos de emisión incorrectos</option>
                                <option value="4">factura devuelta</option>
                            </select>
                        </div>

                        <div className="space-y-2">
                            <label htmlFor="cancellationReason" className="text-[12px] font-medium text-gray-700">
                                Descripción
                            </label>
                            <textarea
                                id="cancellationReason"
                                value={cancellationReason}
                                onChange={(event) => {
                                    setCancellationReason(event.target.value);
                                    setAnnulErrorMessage(null);
                                }}
                                rows={4}
                                placeholder="Ingrese la descripción del motivo de anulación"
                                className="w-full px-3 py-2 text-[13px] text-gray-700 bg-white border border-gray-200 rounded-lg outline-none transition-colors resize-none placeholder:text-gray-400 focus:border-[#4f6df5]"
                            />
                        </div>

                        {annulErrorMessage && (
                            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-[12px] text-red-700">
                                {annulErrorMessage}
                            </div>
                        )}
                    </div>

                    <DialogFooter className="pt-2">
                        <button
                            type="button"
                            onClick={closeAnnulModal}
                            className="px-3 py-2 text-[12px] font-medium text-gray-700 bg-white border border-gray-200 rounded-md transition-colors hover:bg-gray-50"
                        >
                            Cancelar
                        </button>
                        <button
                            type="button"
                            onClick={() => {
                                if (!annulInvoiceId) {
                                    return;
                                }

                                void handleAnnulInvoice(
                                    annulInvoiceId,
                                    cancellationReasonCode,
                                    cancellationReason
                                );
                            }}
                            className="px-3 py-2 text-[12px] font-semibold text-white bg-red-600 border border-red-600 rounded-md transition-colors hover:bg-red-700"
                        >
                            Aceptar
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default FacturacionPage;